import { SliceState } from "../../../utils/Utils";
import {createAsyncThunk, createSelector, createSlice} from "@reduxjs/toolkit";
import { fetchAll, CriteriaAndPaging } from "./HoldFlagAPI";
import { RootState } from "../../../app/store";
import {HoldFlag, HoldFlagFetchSearchCriteria, HoldFlagReason, HoldFlagStatus} from "./HoldsFlag";
import {isEmpty} from "lodash";
import {apiSlice} from "../../../app/apiSlice";
import {defaultStorage} from "../../../persistence";

export interface HoldFlagState {
    holdFlags?: HoldFlag[];
    status: SliceState;
    canGetMore: boolean;
    holdFlagReasons: HoldFlagReason[];
    holdFlagReasonsLoading: boolean;
    holdFlagStatuses?: HoldFlagStatus[];
    holdFlagStatusesLoading: boolean;
    criteria: HoldFlagFetchSearchCriteria;
}

export const HOLD_FLAGS_CRITERIA_STORAGE_KEY =  'hold-flag-search-criteria';

// Initialize the criteria from storage
const persistedCriteriaJson = defaultStorage.getItem(HOLD_FLAGS_CRITERIA_STORAGE_KEY);
const persistedCriteria = persistedCriteriaJson 
    ? JSON.parse(persistedCriteriaJson) as HoldFlagFetchSearchCriteria 
    : {};

const initialState: HoldFlagState = {
    holdFlags: undefined,
    status: 'idle',
    canGetMore: true,
    holdFlagReasons: [],
    holdFlagReasonsLoading: false,
    holdFlagStatuses: [],
    holdFlagStatusesLoading: false,
    criteria: persistedCriteria,
};

export const fetchHoldFlags = createAsyncThunk(
    'holdFlags/fetchAll',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

export const fetchMoreHoldFlags = createAsyncThunk(
    'holdFlags/fetchMore',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

export const refetchAllHoldFlags = createAsyncThunk(
    'holdFlags/refetch',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

function getDefaultHoldFlagReasonIds(reasons: readonly HoldFlagReason[]) {
    return reasons
        .map(r => r.id);
}

function getDefaultHoldFlagStatusIds(payload: readonly HoldFlagStatus[] = []) {
    return payload
        .filter(s => s.name !== 'Resolved')
        .map(s => s.id);
}

export const holdFlagSlice = createSlice({
    name: 'holdFlag',
    initialState,
    reducers: {
        changeSearch: state => {
            state.status = 'idle';
        },
        setHoldFlagSearchCriteria: (state, {payload}) => {
            state.criteria = payload;
        },
        resetHoldFlagSearchCriteria: state => {
            state.criteria = {
                holdFlagReasonIds: getDefaultHoldFlagReasonIds(state.holdFlagReasons),
                holdFlagStatusIds: getDefaultHoldFlagStatusIds(state.holdFlagStatuses)
            };
            state.holdFlags = undefined;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchHoldFlags.pending, state => {
                state.status = 'loading'
                state.holdFlags = [];
            })
            .addCase(fetchHoldFlags.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.holdFlags = action.payload;
                state.canGetMore = !isEmpty(action.payload);
            })
            .addCase(fetchHoldFlags.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            })

            // fetch more
            .addCase(fetchMoreHoldFlags.pending, state => {
                state.status = 'loading'
            })
            .addCase(fetchMoreHoldFlags.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (action.payload.length) state.holdFlags = state.holdFlags?.concat(action.payload);
                state.canGetMore = !isEmpty(action.payload);
            })
            .addCase(fetchMoreHoldFlags.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            })

            // refetch
            .addCase(refetchAllHoldFlags.pending, state => {
                state.status = 'loading'
                state.holdFlags = [];
            })
            .addCase(refetchAllHoldFlags.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.holdFlags = action.payload;
                state.canGetMore = !isEmpty(action.payload);
            })
            .addCase(refetchAllHoldFlags.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            })
        
            // get hold flag reasons
            .addMatcher(
                apiSlice.endpoints.getHoldFlagReasons.matchPending,
                state => {
                    state.holdFlagReasonsLoading = true;
                }
            )
            .addMatcher(
                apiSlice.endpoints.getHoldFlagReasons.matchFulfilled,
                (state, {payload}) => {
                    state.holdFlagReasonsLoading = false;
                    state.holdFlagReasons = payload;
                    // Set the defaults if they have not been set
                    if (!state.criteria.holdFlagReasonIds) {
                        state.criteria.holdFlagReasonIds = getDefaultHoldFlagReasonIds(payload);
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getHoldFlagReasons.matchRejected,
                state => {
                    state.holdFlagReasonsLoading = false;
                }
            )
        

            // get hold flag statuses
            .addMatcher(
                apiSlice.endpoints.getHoldFlagStatuses.matchPending,
                (state) => {
                    state.holdFlagStatusesLoading = true;
                }
            )
            .addMatcher(
                apiSlice.endpoints.getHoldFlagStatuses.matchFulfilled,
                (state, {payload}) => {
                    state.holdFlagStatusesLoading = false;
                    state.holdFlagStatuses = payload;
                    // Set the defaults if they have not been set
                    if (!state.criteria.holdFlagStatusIds) {
                        state.criteria.holdFlagStatusIds = getDefaultHoldFlagStatusIds(payload);
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getHoldFlagStatuses.matchRejected,
                (state) => {
                    state.holdFlagStatusesLoading = false;
                }
            )
        ;
    }
});

// Fetch hold flags
export const selectHoldFlags = (state: RootState) => state.holdFlags.holdFlags;
export const canFetchMoreHoldFlags = (state: RootState) => state.holdFlags.canGetMore;
export const isFetchingHoldFlags = (state: RootState) => state.holdFlags.status === 'loading';

export const selectHoldFlagReasons = (state: RootState) => state.holdFlags.holdFlagReasons;
export const selectHoldFlagReasonsLoading = (state: RootState) => state.holdFlags.holdFlagReasonsLoading;
export const selectHoldFlagStatuses = (state: RootState) => state.holdFlags.holdFlagStatuses;
export const selectHoldFlagStatusesLoading = (state: RootState) => state.holdFlags.holdFlagStatusesLoading;

/**
 * A selector function to compute search criteria based on the hold flag state.
 * It extracts the `criteria` from the `holdFlags` state and evaluates if the `orderId`
 * property exists. If so, it returns an object containing only the `orderId`.
 * If `orderId` is absent, it returns the entire `criteria` object.
 *
 * Utilized in state management to optimize derived data calculations and prevent unnecessary re-renders.
 *
 * Dependencies:
 * - `state.holdFlags.criteria`: The hold flags' criteria from the application's state.
 */
export const selectHoldFlagSearchCriteria = createSelector([
        (state: RootState) => state.holdFlags.criteria
    ],
    criteria => criteria.orderId
        ? {orderId: criteria.orderId}
        : criteria
);

const { actions, reducer } = holdFlagSlice;
export const { 
    changeSearch,
    setHoldFlagSearchCriteria,
    resetHoldFlagSearchCriteria,
} = actions;
export default reducer;